import {
  generateOAuthUrl,
  type OAuthAccessLevel,
} from "@newform-monorepo/backend/src/lib/oauth/oauth-scopes";
import type { AccessCapability } from "@newform-monorepo/backend/src/lib/services/platformAccess/registry";
import type { Platform } from "@newform-monorepo/db";
import { isPlatformName, PLATFORM_METADATA } from "@/lib/platformRegistry";

/** The ad account row an OAuth round trip was for, on the page it returns to. */
export const OAUTH_ACCOUNT_PARAM = "adAccountId";
/** Why the round trip didn't complete (an {@link OAuthErrorReason}). */
export const OAUTH_ERROR_PARAM = "connectError";
/** Which platform the round trip was with, so the message can name it. */
export const OAUTH_PLATFORM_PARAM = "connectPlatform";

const OAUTH_ERROR_MESSAGES = {
  cancelled: (name: string) =>
    `${name} wasn't connected, so nothing changed. Reconnect when you're ready.`,
  failed: (name: string) =>
    `Couldn't save the ${name} connection. Try reconnecting, and contact support if it keeps failing.`,
};

export type OAuthErrorReason = keyof typeof OAUTH_ERROR_MESSAGES;

function isOAuthErrorReason(value: string): value is OAuthErrorReason {
  return Object.hasOwn(OAUTH_ERROR_MESSAGES, value);
}

/**
 * The message for a round trip that didn't complete. The URL carries only a
 * reason code and a platform, never the text: anyone can craft a link, and a
 * toast should only ever say something we wrote. Null for anything else.
 */
export function oauthErrorMessage(
  reason: string | null,
  platform: string | null,
): string | null {
  if (!reason || !platform || !isOAuthErrorReason(reason)) return null;
  if (!isPlatformName(platform)) return null;
  return OAUTH_ERROR_MESSAGES[reason](PLATFORM_METADATA[platform].name);
}

/**
 * Where an OAuth round trip comes back to: the page it started on, with the
 * account it was for, so that page can reopen it. A leftover error from an
 * earlier attempt is dropped.
 */
export function oauthReturnPath(
  pathname: string,
  search: string | URLSearchParams | null,
  adAccountId: string,
): string {
  const params = new URLSearchParams(search ?? undefined);
  params.set(OAUTH_ACCOUNT_PARAM, adAccountId);
  params.delete(OAUTH_ERROR_PARAM);
  params.delete(OAUTH_PLATFORM_PARAM);
  return `${pathname}?${params.toString()}`;
}

export function getPlatformConnectUrl(
  platform: Platform,
  slug: string,
  /** An in-app path from {@link oauthReturnPath}. */
  returnTo: string,
  accountId: string,
  accessLevel?: OAuthAccessLevel,
  options: {
    /** Capabilities the reconnect exists to add, on top of the baseline. */
    capabilities?: readonly AccessCapability[];
    /** Ask again for permissions the user declined before (Meta). */
    rerequest?: boolean;
  } = {},
): string {
  // AppLovin authenticates with a report key pasted into the account form, so
  // there is no authorization URL to send anyone to.
  if (!isPlatformName(platform)) {
    throw new Error(`No connect URL for platform ${platform}`);
  }
  return generateOAuthUrl(platform, {
    slug,
    returnTo,
    accountId,
    accessLevel,
    capabilities: options.capabilities,
    rerequest: options.rerequest,
  });
}
